import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { seasonsApi, Season } from '../api/seasons';
import { useTranslation } from 'react-i18next';

const STATUS_STYLE: Record<string, string> = {
  open: 'bg-green-100 text-green-700 border-green-300',
  active: 'bg-blue-100 text-blue-700 border-blue-300',
  finished: 'bg-gray-100 text-gray-500 border-gray-300',
  archived: 'bg-purple-100 text-purple-700 border-purple-300',
};

const CLASS_STYLE: Record<string, string> = {
  A: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  B: 'bg-blue-100 text-blue-800 border-blue-300',
  C: 'bg-gray-100 text-gray-600 border-gray-300',
};

function SeasonCard({ season }: { season: Season }) {
  const { t } = useTranslation();
  const start = new Date(season.start_date).toLocaleDateString('de-DE');
  const end = new Date(season.end_date).toLocaleDateString('de-DE');

  return (
    <Link to={`/seasons/${season.id}`}
      className="card hover:shadow-md transition-shadow block group overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(255,255,255,1) 60%, rgba(255,255,255,0.1) 100%), url(/images/${season.season_type === 'pairs' ? 'paar' : 'einzel'}.png)`,
        backgroundSize: 'auto 220%', backgroundPosition: 'right 35%',
      }}>
      <div style={{ maxWidth: '60%' }}>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
            {season.name}
          </h3>
          <span className={`badge border text-xs ${CLASS_STYLE[season.season_class] ?? ''}`}>
            {t('season.class')} {season.season_class}
          </span>
          <span className={`badge border text-xs ${STATUS_STYLE[season.status] ?? ''}`}>
            {t(`season.status_${season.status}`)}
          </span>
          {season.is_registered && (
            <span className="badge bg-primary-100 text-primary-700 border-primary-300 text-xs">
              {t('season.registered')}
            </span>
          )}
          {season.max_participants != null && season.participant_count != null && season.participant_count >= season.max_participants && (
            <span className="badge bg-red-100 text-red-700 border-red-300 text-xs">Voll</span>
          )}
        </div>
        {season.description && (
          <p className="text-sm text-gray-600 line-clamp-1">{season.description}</p>
        )}
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
          <span>{start} – {end}</span>
          <span>{season.num_rounds} {t('season.rounds')}</span>
          <span>
            {season.participant_count ?? 0}
            {season.max_participants != null ? ` / ${season.max_participants}` : ''} {t('season.participants')}
          </span>
          {season.fee ? <span className="font-medium text-gray-700">{season.fee} €</span> : <span>{t('leagueList.free')}</span>}
          {season.elo_min && <span>ELO ≥ {season.elo_min}</span>}
          {season.elo_max && <span>ELO ≤ {season.elo_max}</span>}
          {season.region && <span>📍 {season.region}</span>}
        </div>
      </div>
    </Link>
  );
}

function EloInfoButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        onBlur={e => { if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700"
      >
        <span>{t('season.eloInfoButton')}</span>
        <span className="w-4 h-4 rounded-full bg-gray-200 text-[10px] font-bold flex items-center justify-center">?</span>
      </button>
      {open && (
        <>
          {/* Sprechblasen-Pfeil nach oben (zeigt auf Button) */}
          <div className="absolute top-full right-5 z-50 w-0 h-0 mt-[1px]"
            style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #e5e7eb' }}>
            <div className="absolute top-[2px] left-[-7px] w-0 h-0"
              style={{ borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: '7px solid white' }} />
          </div>
          {/* Sprechblase */}
          <div className="absolute top-full right-0 mt-3 z-50 w-96 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 text-left">
            <p className="text-sm font-bold text-gray-900 mb-2">{t('season.eloInfoButton')}</p>
            <p className="text-xs text-gray-600 mb-3">{t('season.eloInfoIntro')}</p>
            <div className="space-y-1.5 text-xs mb-4">
              <div className="flex gap-2">
                <span className="text-green-600 font-bold w-4">+</span>
                <span className="text-gray-700"><strong>{t('season.eloWinStronger')}</strong> → {t('season.eloWinStrongerResult')}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-green-600 font-bold w-4">+</span>
                <span className="text-gray-700"><strong>{t('season.eloWinWeaker')}</strong> → {t('season.eloWinWeakerResult')}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-red-500 font-bold w-4">−</span>
                <span className="text-gray-700"><strong>{t('season.eloLoseWeaker')}</strong> → {t('season.eloLoseWeakerResult')}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-red-500 font-bold w-4">−</span>
                <span className="text-gray-700"><strong>{t('season.eloLoseStronger')}</strong> → {t('season.eloLoseStrongerResult')}</span>
              </div>
            </div>
            <div className="space-y-3 pt-3 border-t border-gray-100 text-xs">
              <div>
                <p className="font-semibold text-gray-800 mb-0.5">{t('season.eloChangeTitle')}</p>
                <p className="text-gray-500" dangerouslySetInnerHTML={{ __html: t('season.eloChangeText') }} />
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-0.5">{t('season.eloWalkoverTitle')}</p>
                <p className="text-gray-500" dangerouslySetInnerHTML={{ __html: t('season.eloWalkoverText') }} />
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-0.5">{t('season.eloFloorTitle')}</p>
                <p className="text-gray-500" dangerouslySetInnerHTML={{ __html: t('season.eloFloorText') }} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function SeasonList() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');

  const { data: seasons = [], isLoading } = useQuery({
    queryKey: ['seasons', statusFilter],
    queryFn: () => seasonsApi.list(statusFilter || undefined).then(r => r.data),
  });

  const STATUS_FILTERS = [
    { value: '', label: t('leagueList.filterAll') },
    { value: 'open', label: t('season.status_open') },
    { value: 'active', label: t('season.status_active') },
    { value: 'finished', label: t('season.status_finished') },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('season.listTitle')}</h1>
      <p className="text-gray-500 text-sm mb-6">{t('season.listSubtitle')}</p>

      <div className="flex items-center gap-2 mb-6">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
          {STATUS_FILTERS.map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === f.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <EloInfoButton />
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="card animate-pulse h-24" />)}</div>
      ) : seasons.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t('season.empty')}</div>
      ) : (
        <div className="space-y-4">
          {seasons.map(s => <SeasonCard key={s.id} season={s} />)}
        </div>
      )}
    </div>
  );
}
